import { useEffect, useState } from "react";
import type {
  AccountingLocation,
  ExpenseCategory,
  ExpenseChange,
  ExpenseRow,
  SaveExpense,
} from "@dentacore/shared-types";

import { listExpenseHistory } from "../api/accounting";
import { parseMoney } from "../patients/money";
import { Modal } from "../ui/modal";
import { NumberField } from "../ui/number-field";
import { describe } from "../settings/use-writer";
import { amountProblem, money, moneyInput, niceDay, niceMoment } from "./format";
import styles from "./accounting.module.css";
import { RequiredMark } from "../ui/required-mark";

/** The location select's value for an expense shared by every location (AC-D9). */
const SHARED = "SHARED";

type Snapshot = NonNullable<ExpenseChange["after"]>;

/**
 * *Add expense* and *Edit expense* (AC-D4, AC-D9, AC-D21).
 *
 * **The date is the day it was paid, and never later than today** — an expense
 * counts in the month it was paid, so a future one would sit in a month that
 * has not happened. *Shared by all locations* splits it across them by the
 * team's rota hours that month.
 *
 * Editing offers **Delete, behind a second step inside the dialog** — the
 * expense comes off every total it was in — and shows the expense's history
 * from the audit log, because a figure that changes the year's profit should
 * say who changed it and from what.
 */
export function ExpenseDialog({
  expense,
  categories,
  locations,
  defaultBranchId,
  today,
  homeCurrency,
  busy,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
  startOnDelete = false,
}: {
  /** Null to add one. */
  expense: ExpenseRow | null;
  categories: readonly ExpenseCategory[];
  locations: readonly AccountingLocation[];
  /** Where a new expense starts — the first location selected on the page. */
  defaultBranchId: string | null;
  today: string;
  /** What a shared expense is in (QA4-D6); a location's is in that location's. */
  homeCurrency: string;
  busy: boolean;
  /** Each resolves true once the write landed, so the dialog closes only then. */
  onCreate: (body: SaveExpense) => Promise<boolean>;
  onUpdate: (changes: Partial<SaveExpense>) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  onClose: () => void;
  /**
   * Open on the delete step — the quick ring's *Delete* (STORY-221). It is the
   * same second step the form's *Delete* leads to, so it still asks first.
   */
  startOnDelete?: boolean;
}): React.JSX.Element {
  const [location, setLocation] = useState(
    expense === null ? (defaultBranchId ?? SHARED) : (expense.branchId ?? SHARED),
  );
  /**
   * **The amount is in the currency it will be recorded in** (QA4-D6): a shared
   * expense's is the home currency, a location's that location's — and one
   * already recorded keeps its own while it stays where it is.
   */
  const currency =
    expense !== null && (expense.branchId ?? SHARED) === location
      ? expense.currency
      : location === SHARED
        ? homeCurrency
        : (locations.find((row) => row.id === location)?.currency ?? homeCurrency);
  const [amount, setAmount] = useState(
    expense === null ? "" : moneyInput(expense.amountCents, expense.currency),
  );
  const [paidOn, setPaidOn] = useState(expense?.paidOn ?? today);
  const [categoryId, setCategoryId] = useState(expense?.categoryId ?? categories[0]?.id ?? "");
  const [note, setNote] = useState(expense?.note ?? "");
  const [confirmDelete, setConfirmDelete] = useState(startOnDelete);
  const [history, setHistory] = useState<readonly ExpenseChange[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const expenseId = expense?.id ?? null;
  useEffect(() => {
    if (expenseId === null) return;
    const controller = new AbortController();
    listExpenseHistory(expenseId, controller.signal)
      .then(setHistory)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setHistory([]);
        setHistoryError(describe(cause, "Its history could not be read."));
      });
    return () => controller.abort();
  }, [expenseId]);

  const categoryName = (id: string): string =>
    categories.find((category) => category.id === id)?.name ?? "a removed category";
  const locationName = (id: string | null): string =>
    id === null ? "Shared" : (locations.find((row) => row.id === id)?.name ?? "Another location");

  const amountError = amountProblem(amount, currency);
  const dateError =
    paidOn === ""
      ? "Choose the date it was paid."
      : paidOn > today
        ? "That date is in the future. Record an expense once it is paid."
        : null;
  const categoryError = categoryId === "" ? "Choose a category." : null;
  const blocked = amountError !== null || dateError !== null || categoryError !== null;

  /* ── delete, the second step ──────────────────────────────────────────── */

  if (expense !== null && confirmDelete) {
    return (
      <Modal label="Delete this expense?" onClose={onClose} testId="expense-dialog">
        <div className={styles.dialogForm}>
          <h2 className={styles.dialogTitle}>Delete this expense?</h2>
          <p className={styles.dialogSub}>
            {[
              expense.note ?? "",
              `${money(expense.amountCents, currency)} on ${niceDay(expense.paidOn)}`,
            ]
              .filter((part) => part !== "")
              .join(" · ")}
          </p>
          <p className={styles.note}>It comes off every total it was in. This cannot be undone.</p>
          <div className={styles.dialogActions}>
            <button type="button" className={styles.ghost} onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.danger}
              disabled={busy}
              onClick={() => {
                void onDelete().then((landed) => {
                  if (landed) onClose();
                });
              }}
              data-testid="expense-delete-confirm"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  /* ── the form ─────────────────────────────────────────────────────────── */

  const submit = (): void => {
    const cents = parseMoney(amount, currency);
    if (blocked || cents === null) return;
    const body: SaveExpense = {
      paidOn,
      categoryId,
      branchId: location === SHARED ? null : location,
      amountCents: cents,
      note: note.trim() === "" ? null : note.trim(),
    };

    const close = (landed: boolean): void => {
      if (landed) onClose();
    };

    if (expense === null) {
      void onCreate(body).then(close);
      return;
    }

    // Only what changed, so the history records the edit that was made.
    const changes: Partial<SaveExpense> = {};
    if (body.amountCents !== expense.amountCents) changes.amountCents = body.amountCents;
    if (body.paidOn !== expense.paidOn) changes.paidOn = body.paidOn;
    if (body.categoryId !== expense.categoryId) changes.categoryId = body.categoryId;
    if (body.branchId !== expense.branchId) changes.branchId = body.branchId;
    if ((body.note ?? null) !== (expense.note ?? null)) changes.note = body.note ?? null;
    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }
    void onUpdate(changes).then(close);
  };

  // An expense at a location this person cannot pick stays where it is.
  const outside =
    expense !== null &&
    expense.branchId !== null &&
    !locations.some((row) => row.id === expense.branchId);

  return (
    <Modal
      label={expense === null ? "Add expense" : "Edit expense"}
      onClose={onClose}
      testId="expense-dialog"
    >
      <form
        className={styles.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <h2 className={styles.dialogTitle}>{expense === null ? "Add expense" : "Edit expense"}</h2>
        <p className={styles.dialogSub}>Counts in monthly and yearly profit, never in a day.</p>

        <div className={styles.dialogGrid}>
          <NumberField
            label="Amount"
            required
            className={styles.field}
            labelClassName={styles.fieldLabel}
            inputClassName={styles.input}
            value={amount}
            onChange={setAmount}
            rules={{ min: 0 }}
            placeholder="0.00"
            autoFocus
            testId="expense-amount"
          />

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Date paid
              <RequiredMark />
            </span>
            <input
              type="date"
              className={styles.input}
              value={paidOn}
              max={today}
              onChange={(event) => setPaidOn(event.target.value)}
              data-testid="expense-date"
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Category
              <RequiredMark />
            </span>
            <select
              className={styles.input}
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              disabled={categories.length === 0}
              data-testid="expense-category"
            >
              {categories.length === 0 && <option value="">No categories yet</option>}
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Location</span>
            <select
              className={styles.input}
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              data-testid="expense-location"
            >
              {locations.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
              {outside && expense.branchId !== null && (
                <option value={expense.branchId}>Another location</option>
              )}
              <option value={SHARED}>Shared by all locations</option>
            </select>
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Note</span>
          <input
            className={styles.input}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            data-testid="expense-note"
          />
        </label>

        <p className={styles.note}>
          Shared is split across locations by the team&rsquo;s rota hours that month.
        </p>

        {categories.length === 0 && (
          <p className={styles.error}>
            There are no categories yet. Add one under Expenses → Categories first.
          </p>
        )}
        {amount.trim() !== "" && amountError !== null && (
          <p className={styles.error}>{amountError}</p>
        )}
        {dateError !== null && <p className={styles.error}>{dateError}</p>}

        <div className={styles.dialogActions}>
          {expense !== null && (
            <button
              type="button"
              className={`${styles.danger} ${styles.pushLeft}`}
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
              data-testid="expense-delete"
            >
              Delete
            </button>
          )}
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy || blocked}
            data-testid="expense-save"
          >
            {expense === null ? "Add expense" : "Save"}
          </button>
        </div>

        {expense !== null && (
          <div className={styles.history} data-testid="expense-history">
            <h3 className={styles.sectionTitle}>History</h3>
            {history === null ? (
              <p className={styles.note}>Loading…</p>
            ) : historyError !== null ? (
              <p className={styles.error}>{historyError}</p>
            ) : history.length === 0 ? (
              <p className={styles.note}>No changes recorded.</p>
            ) : (
              history.map((change, i) => (
                <div key={`${change.at}-${String(i)}`} className={styles.historyItem}>
                  <span className={styles.historyWhen}>
                    {niceMoment(change.at)} · {change.actorName ?? "Someone"}
                  </span>
                  <span className={styles.historyChange}>
                    {describeChange(change, { currency, categoryName, locationName })}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}

/** One audit row as a sentence: what was added, or what changed from what. */
function describeChange(
  change: ExpenseChange,
  names: {
    currency: string;
    categoryName: (id: string) => string;
    locationName: (id: string | null) => string;
  },
): string {
  const show = (field: keyof Snapshot, snapshot: Snapshot): string => {
    switch (field) {
      case "amountCents":
        return money(snapshot.amountCents, names.currency);
      case "paidOn":
        return niceDay(snapshot.paidOn);
      case "categoryId":
        return names.categoryName(snapshot.categoryId);
      case "branchId":
        return names.locationName(snapshot.branchId);
      case "note":
        return snapshot.note === null || snapshot.note === "" ? "no note" : `“${snapshot.note}”`;
    }
  };

  if (change.action === "CREATED" && change.after !== null) {
    return `Added: ${show("amountCents", change.after)} on ${show("paidOn", change.after)}, ${show("categoryId", change.after)}, ${show("branchId", change.after)}.`;
  }
  if (change.action === "DELETED") return "Deleted.";
  if (change.before === null || change.after === null) return "Changed.";

  const labels: Record<keyof Snapshot, string> = {
    amountCents: "Amount",
    paidOn: "Date paid",
    categoryId: "Category",
    branchId: "Location",
    note: "Note",
  };
  const fields: (keyof Snapshot)[] = ["amountCents", "paidOn", "categoryId", "branchId", "note"];
  const before = change.before;
  const after = change.after;
  const parts = fields
    .filter((field) => (before[field] ?? null) !== (after[field] ?? null))
    .map((field) => `${labels[field]} ${show(field, before)} → ${show(field, after)}`);
  return parts.length === 0 ? "Saved with no change." : `${parts.join("; ")}.`;
}
